import { useDebouncedValue } from "hooks";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { TableFilter } from "ui";
import cn from "classnames";

import {
  AITicket,
  ESupportTicketPriority,
  ESupportTicketSource,
  ESupportTicketStatus,
  TicketStatus,
} from "../../aiTickets.types";
import {
  mapTicket,
  mapUiStatusToBe,
  pickSelectedTicket,
} from "../../aiTickets.mapper";
import { useGetTicket, useGetTickets, useUpdateTicket } from "../../apis";
import { ChannelIcon } from "../../components/ChannelIcon";
import {
  AssignedAgentCell,
  StatusCell,
  TypeCell,
} from "../../components/TicketCells";
import { useSupportTicketsRealTime } from "../../hooks";
import TicketsFilterContent from "./TicketsFilterContent";
import { TicketModal } from "../TicketModal";

import styles from "./AITickets.module.scss";

const OpenIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M4 5a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-4l-3 3-3-3H6a2 2 0 0 1-2-2V5Z"
      fill="#03BF9D"
    />
  </svg>
);

const ResolvedIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 3l1.9 5.6L19.5 10 13.9 11.4 12 17l-1.9-5.6L4.5 10l5.6-1.4L12 3Z"
      fill="#733FB7"
    />
  </svg>
);

const HighPriorityIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" fill="#FB3973" />
    <path d="M12 7v6" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
    <circle cx="12" cy="16.5" r="1.1" fill="#fff" />
  </svg>
);

const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path
      d="M16 16l-3.5-3.5M14 8a6 6 0 1 1-12 0 6 6 0 0 1 12 0Z"
      stroke="#7E7E8B"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SortChevron = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path
      d="M3 4.5L6 7.5L9 4.5"
      stroke="#7E7E8B"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

type Tab = "Active" | "Resolved" | "All";

const ACTIVE_STATUSES = [
  ESupportTicketStatus.Open,
  ESupportTicketStatus.InProgress,
  ESupportTicketStatus.WaitingForCustomer,
];
const RESOLVED_STATUSES = [
  ESupportTicketStatus.Resolved,
  ESupportTicketStatus.Closed,
];

const PAGE_TAKE = 20;

const StatCard = ({
  icon,
  iconBg,
  label,
  value,
}: {
  icon: JSX.Element;
  iconBg: string;
  label: string;
  value: string;
}) => (
  <div className={styles.statCard}>
    <div className={styles.statRow}>
      <div className={styles.iconBox} style={{ backgroundColor: iconBg }}>
        {icon}
      </div>
      <div className={styles.statLabel}>{label}</div>
    </div>
    <div className={styles.statValue}>{value}</div>
  </div>
);

const buildPages = (current: number, total: number): (number | "...")[] => {
  if (total <= 1) return [];
  const pages: (number | "...")[] = [];
  const window = 1;
  for (let p = 1; p <= total; p += 1) {
    if (
      p === 1 ||
      p === total ||
      (p >= current - window && p <= current + window)
    ) {
      pages.push(p);
    } else if (pages[pages.length - 1] !== "...") {
      pages.push("...");
    }
  }
  return pages;
};

const AITickets = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<Tab>("Active");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 400);
  const trimmedSearch = debouncedSearch.trim();
  // `?ticketId=` opens one straight away — the manual-return picker and the
  // « À revoir » page link here so an agent who finds a ticket already
  // covering an order or a reply can go read it instead of raising a second
  // one. That ticket is loaded on its own: it may be resolved (not on the
  // Active tab) or past the first page.
  const [searchParams] = useSearchParams();
  const [linkedTicketId] = useState<string | null>(
    searchParams.get("ticketId"),
  );
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(
    linkedTicketId,
  );
  const linkedTicketQuery = useGetTicket(linkedTicketId);
  const [page, setPage] = useState(1);
  const [priorityFilter, setPriorityFilter] = useState<
    ESupportTicketPriority[]
  >([]);
  const [sourceFilter, setSourceFilter] = useState<ESupportTicketSource[]>([]);

  useSupportTicketsRealTime();

  const sharedFilters = {
    search: trimmedSearch || undefined,
    priorities: priorityFilter.length ? priorityFilter : undefined,
    sources: sourceFilter.length ? sourceFilter : undefined,
  };

  const activeQuery = useGetTickets({
    ...sharedFilters,
    statuses: ACTIVE_STATUSES,
    take: PAGE_TAKE,
    page: activeTab === "Active" ? page : 1,
  });
  const resolvedQuery = useGetTickets({
    ...sharedFilters,
    statuses: RESOLVED_STATUSES,
    take: PAGE_TAKE,
    page: activeTab === "Resolved" ? page : 1,
  });
  const allQuery = useGetTickets({
    ...sharedFilters,
    take: PAGE_TAKE,
    page: activeTab === "All" ? page : 1,
  });

  const currentQuery =
    activeTab === "Active"
      ? activeQuery
      : activeTab === "Resolved"
        ? resolvedQuery
        : allQuery;

  const counts = {
    active: activeQuery.data?.meta.itemCount ?? 0,
    resolved: resolvedQuery.data?.meta.itemCount ?? 0,
    all: allQuery.data?.meta.itemCount ?? 0,
  };

  const highPriority = useMemo(() => {
    const items = allQuery.data?.data ?? [];
    return items.filter(
      (item) =>
        item.priority === ESupportTicketPriority.Urgent ||
        item.priority === ESupportTicketPriority.Alert,
    ).length;
  }, [allQuery.data]);

  const tickets = useMemo<AITicket[]>(
    () => (currentQuery.data?.data ?? []).map(mapTicket),
    [currentQuery.data],
  );

  const pageCount = currentQuery.data?.meta.pageCount ?? 1;
  const pages = buildPages(page, pageCount);

  // reset to first page when the tab / search / filters change
  useEffect(() => {
    setPage(1);
  }, [activeTab, trimmedSearch, priorityFilter, sourceFilter]);

  const updateMutation = useUpdateTicket();

  const selectedTicket = useMemo(
    () =>
      pickSelectedTicket(tickets, selectedTicketId, linkedTicketQuery.data),
    [tickets, selectedTicketId, linkedTicketQuery.data],
  );

  const onChangeStatus = (ticket: AITicket, next: TicketStatus) =>
    updateMutation.mutate({
      ticketId: ticket.id,
      status: mapUiStatusToBe(next),
    });

  const onChangePriority = (ticket: AITicket, next: ESupportTicketPriority) =>
    updateMutation.mutate({ ticketId: ticket.id, priority: next });

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>{t("AI Tickets")}</h1>

      <div className={styles.statsRow}>
        <StatCard
          icon={<OpenIcon />}
          iconBg="#e6f9f5"
          label={t("Open tickets")}
          value={counts.active.toLocaleString()}
        />
        <StatCard
          icon={<ResolvedIcon />}
          iconBg="#f0e9fb"
          label={t("Resolved tickets")}
          value={counts.resolved.toLocaleString()}
        />
        <StatCard
          icon={<HighPriorityIcon />}
          iconBg="#ffe0ea"
          label={t("High priority tickets")}
          value={highPriority.toLocaleString()}
        />
      </div>

      <div className={styles.card}>
        <div className={styles.tabs}>
          {(["Active", "Resolved / Closed", "All"] as const).map((label, i) => {
            const key = (["Active", "Resolved", "All"] as const)[i];
            const translatedLabel = t(label);
            const count =
              key === "Active"
                ? counts.active
                : key === "Resolved"
                  ? counts.resolved
                  : counts.all;
            return (
              <button
                key={label}
                type="button"
                className={cn(styles.tab, {
                  [styles.tabActive]: activeTab === key,
                })}
                onClick={() => setActiveTab(key)}
              >
                {translatedLabel}
                <span
                  className={cn(styles.tabCount, {
                    [styles.tabCountActive]: activeTab === key,
                  })}
                >
                  {count.toLocaleString()}
                </span>
              </button>
            );
          })}
        </div>

        <div className={styles.searchRow}>
          <div className={styles.searchBox}>
            <span className={styles.searchIcon}>
              <SearchIcon />
            </span>
            <input
              type="text"
              className={styles.searchInput}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("Search...")}
            />
          </div>
          <TableFilter>
            {() => (
              <TicketsFilterContent
                priorities={priorityFilter}
                sources={sourceFilter}
                onChangePriorities={setPriorityFilter}
                onChangeSources={setSourceFilter}
              />
            )}
          </TableFilter>
        </div>

        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t("Ticket")}</th>
                <th>{t("Type")}</th>
                <th>{t("Channel")}</th>
                <th>{t("Assigned Agent")}</th>
                <th>
                  <span className={styles.thWithSort}>
                    {t("Status")} <SortChevron />
                  </span>
                </th>
                <th>
                  <span className={styles.thWithSort}>
                    {t("Date created")} <SortChevron />
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((ticket) => (
                <tr
                  key={ticket.id}
                  className={styles.row}
                  onClick={() => setSelectedTicketId(ticket.id)}
                >
                  <td>
                    <div className={styles.ticketCell}>
                      <div className={styles.ticketTitle}>{ticket.title}</div>
                      <div className={styles.ticketDesc}>
                        {ticket.description}
                      </div>
                    </div>
                  </td>
                  <td>
                    <TypeCell
                      priority={ticket.raw.priority}
                      disabled={updateMutation.isLoading}
                      onChange={(p) => onChangePriority(ticket, p)}
                    />
                  </td>
                  <td>
                    <ChannelIcon source={ticket.raw.source} size={24} />
                  </td>
                  <td>
                    <AssignedAgentCell source={ticket.raw.source} />
                  </td>
                  <td>
                    <StatusCell
                      status={ticket.status}
                      disabled={updateMutation.isLoading}
                      onChange={(s) => onChangeStatus(ticket, s)}
                    />
                  </td>
                  <td className={styles.dateCell}>{ticket.dateCreated}</td>
                </tr>
              ))}
              {!currentQuery.isLoading && tickets.length === 0 && (
                <tr>
                  <td colSpan={6} className={styles.dateCell}>
                    {t("No tickets")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {pageCount > 1 && (
          <div className={styles.pagination}>
            {pages.map((p, i) =>
              p === "..." ? (
                <span key={`dots-${i}`} className={styles.pageDots}>
                  ...
                </span>
              ) : (
                <button
                  key={p}
                  type="button"
                  className={cn(styles.pageBtn, {
                    [styles.pageBtnActive]: p === page,
                  })}
                  onClick={() => setPage(p)}
                >
                  {p}
                </button>
              ),
            )}
            <button
              type="button"
              className={cn(styles.pageBtn, styles.pageBtnNext)}
              disabled={page >= pageCount}
              onClick={() => setPage((p) => Math.min(p + 1, pageCount))}
            >
              {t("Next")}
            </button>
          </div>
        )}
      </div>

      {selectedTicket && (
        <TicketModal
          ticket={selectedTicket}
          isOpen
          onClose={() => setSelectedTicketId(null)}
          onStatusChange={(next) => onChangeStatus(selectedTicket, next)}
          onPriorityChange={(next) => onChangePriority(selectedTicket, next)}
        />
      )}
    </div>
  );
};

export default AITickets;
